import React, { useState, useMemo, useEffect } from 'react';
import { Column, Row, Expander } from '@amzn/meridian';

import ConfigPanel from './shared/ConfigPanel';
import { getCycleDisplayName } from '../../utils/helpers';

import './AutomationConfiguration.css';

const FLOW_LABELS = { induct: 'Induct', stow: 'Stow', diverter: 'Divert' };

const Switch = ({ checked, onChange, ariaLabel }) => (
  <button
    type="button"
    className="ac-switch"
    role="switch"
    aria-checked={checked}
    aria-pressed={checked}
    aria-label={ariaLabel}
    onClick={() => onChange(!checked)}
  />
);

// Compact stepper — typed entry plus +/- nudges, all routed through the same
// change handler as the live model.
const Stepper = ({ value, onChange, min = 0, max = Infinity, step = 1, disabled, suffix }) => {
  const num = parseFloat(value);
  const clamp = (n) => Math.max(min, Math.min(max, n));
  const nudge = (delta) => {
    if (disabled) return;
    onChange(String(clamp((isNaN(num) ? 0 : num) + delta)));
  };
  return (
    <div className={`ac-stepper${disabled ? ' is-disabled' : ''}`}>
      <button type="button" tabIndex={-1} disabled={disabled} onClick={() => nudge(-step)} aria-label="Decrease">
        −
      </button>
      <input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max === Infinity ? undefined : max}
        disabled={disabled}
      />
      <button type="button" tabIndex={-1} disabled={disabled} onClick={() => nudge(step)} aria-label="Increase">
        +
      </button>
      {suffix && <span className="ac-stepper-suffix">{suffix}</span>}
    </div>
  );
};

const AutomationConfiguration = ({
  automationConfig,
  setAutomationConfig,
  availableCycles = [],
  cycleAutomationConfig = null,
  isCycleLevelAutomation = false,
  onCycleAutomationConfigChange,
  joined = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const sortedCycles = useMemo(() => [...availableCycles].sort(), [availableCycles]);
  const [selectedCycle, setSelectedCycle] = useState(sortedCycles[0] || 'CYCLE_1');

  // Keep the selected cycle valid as availableCycles changes between plans.
  useEffect(() => {
    setSelectedCycle((prev) => (sortedCycles.length > 0 && !sortedCycles.includes(prev) ? sortedCycles[0] : prev));
  }, [sortedCycles]);

  // The config currently being shown/edited: the selected cycle's sub-config when
  // cycle-level automation is available, otherwise the single flat config.
  // TOOD: This fallback should eventually be removed (present only for backward compatibility)
  const activeConfig = (isCycleLevelAutomation && cycleAutomationConfig?.[selectedCycle]) || automationConfig;

  // Routes a full-config update to the cycle map (selected cycle) or the flat setter.
  // Guard mirrors activeConfig exactly: only write to the cycle map when the selected
  // cycle actually exists in it, so a momentarily-stale selectedCycle can't write flat
  // data under a wrong key (read and write stay in sync).
  const commitConfig = (nextConfig) => {
    if (isCycleLevelAutomation && cycleAutomationConfig?.[selectedCycle]) {
      onCycleAutomationConfigChange({ ...cycleAutomationConfig, [selectedCycle]: nextConfig });
    } else {
      setAutomationConfig(nextConfig);
    }
  };

  const updateMode = (flow, executionType, field, value) => {
    const newModes = activeConfig[flow].map((m) =>
      m.executionType === executionType ? { ...m, [field]: value } : { ...m }
    );
    commitConfig({ ...activeConfig, [flow]: newModes });
  };

  const handleLineCountChange = (executionType, value) => {
    if (value === '') return updateMode('induct', executionType, 'lineCount', null);
    const n = parseInt(value);
    if (!isNaN(n)) updateMode('induct', executionType, 'lineCount', n);
  };

  const handlePriorityChange = (executionType, value) => {
    if (value === '') return updateMode('induct', executionType, 'priority', null);
    const n = parseInt(value);
    if (isNaN(n)) return;
    // Priority is a 1..N fill order, where N is the number of Induct line types —
    // there's no meaningful rank beyond the last type, so clamp to that range.
    const maxPriority = (activeConfig.induct || []).length;
    updateMode('induct', executionType, 'priority', Math.min(Math.max(n, 1), maxPriority));
  };

  const handlePctChange = (flow, executionType, value) => {
    const pct = value === '' ? null : Math.min(100, Math.max(0, parseFloat(value) || 0));
    const newModes = (activeConfig[flow] || []).map((m) =>
      m.executionType === executionType ? { ...m, volumeSplitPercentage: pct } : { ...m }
    );
    const nonMechSum = newModes
      .filter((m) => m.executionType !== 'MECHANICAL')
      .reduce((s, m) => s + (m.volumeSplitPercentage || 0), 0);
    const mechIdx = newModes.findIndex((m) => m.executionType === 'MECHANICAL');
    if (mechIdx >= 0)
      newModes[mechIdx] = { ...newModes[mechIdx], volumeSplitPercentage: Math.max(0, 100 - nonMechSum) };
    commitConfig({ ...activeConfig, [flow]: newModes });
  };

  const summary = useMemo(() => {
    const nameMap = { MECHANICAL: 'Manual' };
    const items = [];
    (activeConfig.induct || [])
      .filter((m) => m.isEnabled)
      .forEach((m) =>
        items.push({ label: nameMap[m.executionType] || m.executionType, value: `${m.lineCount ?? 0} lines` })
      );
    const adtaStow = (activeConfig.stow || []).find((m) => m.executionType === 'ADTA');
    if (adtaStow) items.push({ label: 'ADTA Stow', value: `${adtaStow.volumeSplitPercentage ?? 0}%` });
    return items;
  }, [activeConfig]);

  const inductModes = activeConfig.induct || [];

  // Every non-induct flow that carries an automation split. Stow today, but the
  // section renders one card per flow so new process groups appear automatically.
  const splitFlows = Object.keys(activeConfig || {}).filter(
    (flow) => flow !== 'induct' && (activeConfig[flow] || []).length > 0
  );

  return (
    <ConfigPanel joined={joined} className="ac-config">
      <Row widths={['fill', 'fit']} alignmentVertical="center">
        <Column spacing="300">
          <Expander
            open={isExpanded}
            onChange={setIsExpanded}
            title="Automation Configuration"
            type="inline"
            spacingInset="300">
            <Column spacing="none" width="100%">
              {/* Cycle selector — only when the plan carries per-cycle automation */}
              {isCycleLevelAutomation && sortedCycles.length > 0 && (
                <div className="ac-cycle-tabs">
                  <div className="fp-config-tabs">
                    {sortedCycles.map((cycle) => (
                      <button
                        key={cycle}
                        type="button"
                        className="fp-config-tab"
                        aria-pressed={selectedCycle === cycle}
                        onClick={() => setSelectedCycle(cycle)}>
                        {getCycleDisplayName(cycle)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Induct — stat tiles */}
              <div className="ac-sub">
                <div className="ac-sub-title">Induct</div>
                <div className="ac-sub-desc">
                  Line capacity per mode. Volume allocated by priority (1 = first) until each is full.
                </div>
                <div className="ac-sub-rule" />
                <div className="ac-tiles" style={{ gridTemplateColumns: `repeat(${inductModes.length || 1}, 1fr)` }}>
                  {inductModes.map((mode) => {
                    const enabled = mode.isEnabled;
                    return (
                      <div key={mode.executionType} className={`ac-tile ${enabled ? 'is-enabled' : 'is-disabled'}`}>
                        <div className="ac-tile-head">
                          <span className="ac-tile-name">{mode.executionType}</span>
                          <Switch
                            checked={enabled}
                            onChange={(v) => updateMode('induct', mode.executionType, 'isEnabled', v)}
                            ariaLabel={`Enable ${mode.executionType}`}
                          />
                        </div>
                        <div className="ac-tile-hero">
                          <span className="ac-tile-hero-num">{mode.lineCount ?? 0}</span>
                          <span className="ac-tile-hero-unit">lines</span>
                        </div>
                        <div className="ac-tile-meta">
                          <div className="ac-tile-field">
                            <span className="ac-field-label">Priority</span>
                            <Stepper
                              value={mode.priority}
                              onChange={(v) => handlePriorityChange(mode.executionType, v)}
                              min={1}
                              max={inductModes.length}
                              disabled={!enabled}
                            />
                          </div>
                          <div className="ac-tile-field">
                            <span className="ac-field-label">Lines</span>
                            <Stepper
                              value={mode.lineCount}
                              onChange={(v) => handleLineCountChange(mode.executionType, v)}
                              min={0}
                              disabled={!enabled}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Automation split — one card per process group (Stow today; more later) */}
              <div className="ac-sub">
                <div className="ac-sub-title">Automation Split</div>
                <div className="ac-sub-desc">Share of volume handled by automation. Manual is auto-calculated.</div>
                <div className="ac-sub-rule" />
                <div className="ac-split-grid">
                  {splitFlows.map((flow) => {
                    const modes = activeConfig[flow] || [];
                    const automated = modes.filter((m) => m.executionType !== 'MECHANICAL');
                    const manual = modes.find((m) => m.executionType === 'MECHANICAL');
                    const manualPct = Math.round(manual?.volumeSplitPercentage ?? 0);
                    const autoPct = Math.round(automated.reduce((s, m) => s + (m.volumeSplitPercentage || 0), 0));
                    return (
                      <div key={flow} className="ac-split-card">
                        <div className="ac-split-card-title">{FLOW_LABELS[flow] || flow}</div>
                        <div className="ac-split">
                          <div className="ac-donut" style={{ '--auto-pct': autoPct }}>
                            <div className="ac-donut-hole">
                              <span className="ac-donut-num">{autoPct}%</span>
                              <span className="ac-donut-label">auto</span>
                            </div>
                          </div>
                          <div className="ac-split-legend">
                            {automated.map((mode) => (
                              <div className="ac-leg-row" key={mode.executionType}>
                                <span className="ac-leg-sw" style={{ background: 'var(--accent-auto)' }} />
                                <span className="ac-leg-name">
                                  {mode.executionType} <span className="muted">(automated)</span>
                                </span>
                                <Stepper
                                  value={mode.volumeSplitPercentage}
                                  onChange={(v) => handlePctChange(flow, mode.executionType, v)}
                                  min={0}
                                  max={100}
                                  suffix="%"
                                />
                              </div>
                            ))}
                            <div className="ac-leg-row">
                              <span className="ac-leg-sw" style={{ background: 'var(--accent-manual)' }} />
                              <span className="ac-leg-name">
                                Mechanical <span className="muted">(manual)</span>
                              </span>
                              <span className="ac-leg-val">{manualPct}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Column>
          </Expander>
        </Column>
        {!isExpanded && summary.length > 0 && (
          <div className="fp-collapsed-summary">
            <div className="fp-collapsed-chips">
              {summary.map((item, idx) => (
                <span key={idx} className="fp-chip">
                  <span className="fp-chip-label">{item.label}</span>
                  <span className="fp-chip-value">{item.value}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </Row>
    </ConfigPanel>
  );
};

export default AutomationConfiguration;
